import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Building2, ArrowLeft, Globe, Linkedin, Users, MapPin, ExternalLink, GitBranch,
} from "lucide-react";
import { openExternal } from "@/lib/utils";

interface AccountDetailHeaderProps {
  company: Record<string, any>;
}

export function AccountDetailHeader({ company }: AccountDetailHeaderProps) {
  const navigate = useNavigate();
  const initials = company.company_name?.split(" ").slice(0, 2).map((w: string) => w[0]).join("").toUpperCase() || "?";
  const location = [company.headquarters_city, company.headquarters_state, company.headquarters_country].filter(Boolean).join(", ");

  // Fetch parent info (if child) or child count (if parent)
  const { data: familyInfo } = useQuery({
    queryKey: ["company-family-header", company.id],
    queryFn: async () => {
      let parentId: string | null = null;
      let parentName: string | null = null;
      let childCount = 0;

      if (company.parent_company_id) {
        const { data: parent } = await supabase
          .from("companies")
          .select("id, company_name")
          .eq("id", company.parent_company_id)
          .single();
        const p = parent as { id: string; company_name: string } | null;
        parentId = p?.id ?? null;
        parentName = p?.company_name ?? null;
      }

      const { count } = await supabase
        .from("companies")
        .select("id", { count: "exact", head: true })
        .eq("parent_company_id", company.id);
      childCount = count ?? 0;

      return { parentId, parentName, childCount };
    },
  });

  return (
    <div className="space-y-4">
      <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2 -ml-2">
        <ArrowLeft className="h-4 w-4" />
        Back to Accounts
      </Button>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-xl font-semibold shrink-0">
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.company_name} className="h-16 w-16 rounded-lg object-cover" />
            ) : (
              initials || <Building2 className="h-6 w-6" />
            )}
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold">{company.company_name}</h1>
            <div className="flex items-center gap-4 text-muted-foreground text-sm">
              {company.employee_range && (
                <span className="flex items-center gap-1"><Users className="h-4 w-4" />{company.employee_range}</span>
              )}
              {location && (
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{location}</span>
              )}
            </div>
            {/* Parent/Child Relationship */}
            {familyInfo?.parentName && familyInfo.parentId && (
              <div className="flex items-center gap-1.5 text-sm">
                <GitBranch className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Subsidiary of</span>
                <Link
                  to={`/accounts/${familyInfo.parentId}`}
                  className="font-medium text-primary hover:underline"
                >
                  {familyInfo.parentName}
                </Link>
              </div>
            )}
            {familyInfo && familyInfo.childCount > 0 && (
              <div className="flex items-center gap-1.5 text-sm">
                <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">
                  Parent company &mdash; {familyInfo.childCount} {familyInfo.childCount === 1 ? "subsidiary" : "subsidiaries"}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {company.domain && (
            <Button variant="outline" size="sm" asChild>
              <a href={`https://${company.domain}`} target="_blank" rel="noopener noreferrer" className="gap-2">
                <Globe className="h-4 w-4" />Website<ExternalLink className="h-3 w-3" />
              </a>
            </Button>
          )}
          {company.linkedin_url && (
            <Button variant="outline" size="sm" className="gap-2" onClick={() => openExternal(company.linkedin_url)}>
              <Linkedin className="h-4 w-4" />LinkedIn
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
